import React, { useEffect, useCallback, useRef } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiEyeOutline } from "@mdi/js";
import { SearchField, Table } from "@heroui/react";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import {
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_COLOR,
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_COLOR,
} from "../constants";
import { SortPanel, SortableColumnHeader } from "./shared";
import { DateRangeFilter } from "@/shared/ui/DateRangeFilter";
import { CombinedFilter } from "@/shared/ui/CombinedFilter";
import { LoadMoreSentinel } from "@/shared/ui/LoadMoreSentinel";
import { ChipWithTooltip } from "@/shared/ui/ChipWithTooltip";
import { useSortPanel } from "@/hooks/useSortPanel";
import { formatMoneyRub } from "@shared-utils/helpers";

const BK_SORT_COLUMNS = [
  { id: "start_time", label: "Дата" },
  { id: "total_cost", label: "Сумма" },
];

const BookingsTab: React.FC = observer(() => {
  const store = contractorDetailStore;
  const sort = useSortPanel("start_time");

  const handleLoadMore = useCallback(() => {
    store.bk.loadMore();
  }, [store]);

  useEffect(() => {
    store.bk.load();
  }, []);

  // Серверная сортировка — при изменении перезагружаем
  useEffect(() => {
    const prefix = sort.sortDescriptor.direction === "descending" ? "-" : "";
    const newSort = `${prefix}${sort.sortDescriptor.column}`;
    if (newSort !== store.bk.sort) {
      store.bk.setSort(newSort);
      store.bk.load(1);
    }
  }, [sort.sortDescriptor]);

  const searchTimer = useRef<number>(0);

  const handleSearch = (val: string) => {
    store.bk.setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => store.bk.load(1), 600);
  };

  const handleStatusFilter = (val: string[]) => {
    store.bk.setStatusFilter(val);
    store.bk.load(1);
  };

  const handlePaymentFilter = (val: string[]) => {
    store.bk.setPaymentFilter(val);
    store.bk.load(1);
  };

  const handleDateRange = (range: { start: string; end: string } | null) => {
    // Сдвигаем конечную дату на +1 день, чтобы последний день включался в выборку
    let endDate = range?.end ?? "";
    if (endDate) {
      const d = new Date(endDate);
      d.setDate(d.getDate() + 1);
      endDate = d.toISOString().slice(0, 10);
    }
    store.bk.setDateRange(range?.start ?? "", endDate);
    store.bk.load(1);
  };

  const handleOpenPayment = (paymentUuid: string, description: string) => {
    store.pd.open(paymentUuid, description, "payment", "booking");
  };

  return (
    <div className="scd-tab-content">
      <div className="scd-toolbar">
        <div className="scd-toolbar__left">
          <SearchField
            value={store.bk.search}
            onChange={handleSearch}
            aria-label="Поиск бронирований"
            className="scd-search"
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder="Клиент, телефон, услуга..." />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>

          <CombinedFilter
            groups={[
              {
                id: "status",
                label: "Статус",
                singleSelect: true,
                options: [
                  { id: "pending", label: "Создан" },
                  { id: "confirmed", label: "Подтверждено" },
                  { id: "in_progress", label: "В работе" },
                  { id: "completed", label: "Завершено" },
                  { id: "cancelled", label: "Отменено" },
                ],
                value: store.bk.statusFilter,
                onChange: handleStatusFilter,
              },
              {
                id: "payment",
                label: "Оплата",
                singleSelect: true,
                options: [
                  { id: "pending", label: "Ожидание" },
                  { id: "paid", label: "Оплачено" },
                  { id: "refunded", label: "Возврат" },
                ],
                value: store.bk.paymentFilter,
                onChange: handlePaymentFilter,
              },
            ]}
            extra={
              <>
                <div className="scd-combined-filter__group-label">Период</div>
                <div style={{ padding: "4px 12px 8px" }}>
                  <DateRangeFilter
                    label="Период бронирований"
                    onChange={handleDateRange}
                  />
                </div>
              </>
            }
          />
        </div>
        <div className="scd-toolbar__right">
          <SortPanel
            columns={BK_SORT_COLUMNS}
            sortDescriptor={sort.sortDescriptor}
            setSortDescriptor={sort.setSortDescriptor}
            isSortOpen={sort.isSortOpen}
            setIsSortOpen={sort.setIsSortOpen}
            sortAnchorRef={sort.sortAnchorRef}
            sortPanelRef={sort.sortPanelRef}
          />
        </div>
      </div>

      <div className="scd-table-wrap">
        <Table variant="secondary">
          <Table.ScrollContainer className="scd-scroll-container">
            <Table.Content
              aria-label="Бронирования"
              className="scd-table scd-table--bookings"
              sortDescriptor={sort.sortDescriptor}
              onSortChange={(d) =>
                sort.setSortDescriptor({
                  column: String(d.column),
                  direction: d.direction as "ascending" | "descending",
                })
              }
            >
              <Table.Header className="scd-table__head scd-table__head--sticky">
                <Table.Column id="start_time" isRowHeader allowsSorting>
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Дата и время
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="client">Клиент</Table.Column>
                <Table.Column id="service">Услуга</Table.Column>
                <Table.Column id="car">Авто</Table.Column>
                <Table.Column
                  id="total_cost"
                  className="scd-table__th_right"
                  allowsSorting
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      Сумма
                    </SortableColumnHeader>
                  )}
                </Table.Column>
                <Table.Column id="payment">Оплата</Table.Column>
                <Table.Column id="status">Статус</Table.Column>
                <Table.Column id="actions" className="scd-table__th--actions">
                  <span className="sr-only">Действия</span>
                </Table.Column>
              </Table.Header>
              <Table.Body>
                {store.bk.items.length === 0 ? (
                  <Table.Row className="scd-table__row">
                    <Table.Cell className="scd-table__td" colSpan={8}>
                      <div className="scd-table-empty">
                        {store.bk.isLoading ? (
                          "Загрузка..."
                        ) : store.bk.error ? (
                          <span className="scd-table-empty__error">
                            Сервер недоступен &mdash; данные не загружены.{" "}
                            <button
                              type="button"
                              className="scd-table-empty__retry"
                              onClick={() => store.bk.load(1)}
                            >
                              Повторить
                            </button>
                          </span>
                        ) : (
                          "Нет данных"
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  store.bk.items.map((b) => {
                    const start = new Date(b.startTime);
                    const end = new Date(b.endTime);
                    const paymentLabel =
                      PAYMENT_STATUS_LABEL[b.paymentStatus] ?? b.paymentStatus;
                    const bookingStatusLabel =
                      BOOKING_STATUS_LABEL[b.status] ?? b.status;
                    const dateFormatted = start.toLocaleDateString("ru-RU", {
                      day: "2-digit",
                      month: "2-digit",
                    });
                    const timeRange = `${start.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}–${end.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`;

                    return (
                      <Table.Row
                        key={b.uuid}
                        id={b.uuid}
                        data-uuid={b.uuid}
                        className="scd-table__row"
                      >
                        <Table.Cell className="scd-table__td scd-table__td--date">
                          <span className="scd-datetime-cell">
                            <span className="scd-datetime-cell__date">
                              {dateFormatted}
                            </span>
                            <span className="scd-datetime-cell__time">
                              {timeRange}
                            </span>
                          </span>
                        </Table.Cell>
                        <Table.Cell className="scd-table__td">
                          <div className="scd-client-cell">
                            <span
                              className="scd-client-cell__name scd-td-truncate"
                              title={b.client.name}
                            >
                              {b.client.name}
                            </span>
                            <span className="scd-client-cell__phone">
                              {b.client.phone}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="scd-table__td">
                          <span
                            className="scd-td-truncate"
                            title={b.service.name}
                          >
                            {b.service.name}
                          </span>
                        </Table.Cell>
                        <Table.Cell className="scd-table__td">
                          <span
                            className="scd-td-truncate"
                            title={`${b.car?.make ?? ""} ${b.car?.model ?? ""}`.trim()}
                          >
                            {b.car?.make} {b.car?.model}
                          </span>
                        </Table.Cell>
                        <Table.Cell className="scd-table__td scd-table__td_right">
                          {formatMoneyRub(b.totalCost)}
                        </Table.Cell>
                        <Table.Cell className="scd-table__td">
                          <ChipWithTooltip
                            label={paymentLabel}
                            color={
                              PAYMENT_STATUS_COLOR[b.paymentStatus] ?? "default"
                            }
                          />
                        </Table.Cell>
                        <Table.Cell className="scd-table__td">
                          <ChipWithTooltip
                            label={bookingStatusLabel}
                            color={BOOKING_STATUS_COLOR[b.status] ?? "default"}
                          />
                        </Table.Cell>
                        <Table.Cell className="scd-table__td scd-table__td--actions">
                          <button
                            type="button"
                            className="scd-eye-btn"
                            aria-label="Просмотр платежа"
                            onClick={() =>
                              b.paymentUuid &&
                              handleOpenPayment(
                                b.paymentUuid,
                                `Платёж за бронирование · ${b.service.name}`,
                              )
                            }
                          >
                            <Icon path={mdiEyeOutline} size={0.8} />
                          </button>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
        <LoadMoreSentinel
          onLoadMore={handleLoadMore}
          isLoading={store.bk.isLoading}
          hasMore={store.bk.meta.hasNextPage}
        />
      </div>
    </div>
  );
});

BookingsTab.displayName = "BookingsTab";
export default BookingsTab;
